export const STORAGE_KEYS = {
  refreshToken: 'maefwk10:refresh',
  fingerprint: 'maefwk10:fp',
  colorScheme: 'maefwk10:color-scheme',
  lang: 'maefwk10:lang',
  tenantId: 'maefwk10:tenant',
  /**
   * «Questa sessione è di un utente-cliente». Persistito perché le route guard girano
   * **sincrone** al primo render, quando il mirror in memoria è ancora vuoto e l'access
   * token non è stato ancora rigenerato dal refresh: senza, su un reload la shell interna
   * fa in tempo a montarsi e a sparare le sue letture, che per un cliente sono tutte 403.
   */
  /**
   * «Questa persona ha un'appartenenza interna» (`associatedTenants` non vuota). Serve alla guardia
   * sincrona per distinguere un cliente puro — che va nel portale — da un **ibrido**, che dopo P8
   * è dipendente in un'azienda e cliente in un'altra e deve partire nel gestionale.
   */
  staffMembership: 'maefwk10:staff-membership',
  portalSession: 'maefwk10:portal',
  /**
   * Il `claimsList` dell'ultima sessione, per **disegnare** il menu al primo render.
   *
   * Stessa ragione di `portalSession`: i claim non stanno nel JWT, arrivano nella busta di
   * login/refresh, e su hard refresh l'access token è solo in memoria — quindi per il tempo del
   * round-trip non sappiamo che permessi ha chi sta guardando. Senza questa copia il menu
   * comparirebbe largo e poi si stringerebbe, cioè mostrerebbe per un istante le voci che è nato
   * per nascondere.
   *
   * ⚠️ Non è un permesso e non autorizza niente: il server resta l'autorità, e ogni
   * login/refresh/cambio-azienda riscrive questa riga (i claim sono per-tenant). Al logout va via.
   */
  claims: 'maefwk10:claims',
  /*
   * `chatbotCorner` non c'e' piu': l'assistente e' un'icona nella testata, non un bottone
   * trascinabile, quindi non ha un angolo da ricordare. La chiave resta scritta nei browser di
   * chi l'aveva usata e non da' fastidio: nessuno la legge.
   */
  /**
   * Nome dell'azienda in cui si è appena entrati, da annunciare **dopo** il ricaricamento.
   * Il cambio azienda ricarica la pagina (è l'unico modo di non portarsi dietro i dati di
   * quella di prima), e un avviso mostrato prima del ricaricamento non lo legge nessuno.
   */
  tenantSwitchNotice: 'maefwk10:tenant-switched',

  /* ───────────────────────── il committente del portale ───────────────────────── */

  /**
   * L'accesso di portale selezionato (`X-Portal-Access`). Vive qui e non in `portalAccess.ts`
   * perché è una chiave di **sessione**: al logout va via, o la prima chiamata del portale del
   * prossimo utente parte con il committente di un altro.
   */
  portalAccessId: 'maefwk10:portal-access-id',

  /**
   * L'id di chi sta guardando, per **intestare le chiavi personali al primo render**.
   *
   * ⚠️ Stessa ragione di `claims` e `portalSession`, ed è la stessa trappola: su hard refresh la
   * rotta monta subito — `isAuthenticatedSync()` dice sì perché il refresh token è persistito —
   * mentre l'access token arriva dopo il round-trip. In quella finestra i componenti leggono già
   * le loro preferenze (`useState(() => leggiChiave(...))`): senza questa copia leggerebbero la
   * chiave **nuda**, cioè niente, e la sede di magazzino o le colonne chiuse del kanban
   * sparirebbero a ogni F5.
   *
   * Non autorizza nulla e non è un'identità: è il nome sotto cui questo browser ha messo le cose
   * di questa persona. Al logout va via con le altre chiavi di sessione.
   */
  userId: 'maefwk10:uid',

  /* ───────────────── preferenze del dispositivo (non della persona) ───────────────── */

  /** Fuso e formato data: specchi locali di `user-preferences`, vince il remoto al login. */
  timezone: 'maefwk10:tz',
  dateFormat: 'maefwk10:date-format',
  /*
   * `mantine-color-scheme-value` **non** è qui: è di Mantine, la scrive e la legge il suo
   * provider, e noi non la tocchiamo più. La leggevamo in un posto solo — per **non** applicare la
   * preferenza del profilo se il browser ne aveva già una — cioè per il difetto che quel giro
   * aveva.
   */
  /** L'ultima e-mail digitata al login. È del **dispositivo** per definizione: serve a chi lo usa. */
  rememberEmail: 'login-remember-email',
  /** Il secondo cancello biometrico: impostazione di questo telefono, non di questa persona. */
  appLock: 'maefwk10:app-lock',
  /** Barra laterale chiusa: stato di questo schermo. */
  navbarCollapsed: 'maefwk10:navbar-collapsed',
  portalNavbarCollapsed: 'maefwk10:portal-navbar-collapsed',
  /** Il tema del tenant, in cache per non ridisegnare la shell al secondo caricamento. */
  uiThemeCache: 'maefwk10:ui-theme-cache',
  /**
   * Strumento da banco di sviluppo: non segue nessuno.
   *
   * ⚠️ `maefwk10:scenario-b` **non** è qui, e non è una dimenticanza: la levetta della
   * simulazione dichiara nel suo commento di stare fuori da questa mappa, perché «questa è la
   * mappa dello stato dell'applicazione» e quella chiave non esiste nel bundle spedito. Il test
   * di disciplina la elenca come deroga.
   */
  portalCustomPagesDev: 'maefwk10:portal-custom-pages-dev',
  /**
   * La coda delle timbrature offline.
   *
   * ⚠️ **Del dispositivo, e deliberatamente**: `durableStore.idrataDaDisco()` la ripesca dal disco
   * **prima** che l'app monti, quando nessuno è ancora entrato — intestarla vorrebbe dire non poter
   * ripristinare al boot proprio la cosa che, persa, non torna. Ogni riga porta il suo `employeeId`,
   * quindi lo svuotamento attribuisce ciascuna timbratura alla persona giusta; a non essere separato
   * era solo *quel che si vede*, e lo separa `useAttendanceQueue` filtrando le righe sull'impiegato
   * corrente.
   */
  attendanceQueue: 'maefwk10:attendance-queue',
  /** Il database locale del foodcost, in attesa del BE. Vedi `features/foodcost/store.ts`. */
  foodcost: 'foodcost:v3',

  /* ───────────────────────── roba della persona ───────────────────────── */

  favoriteProjects: 'maefwk10:favorite-projects',
  /**
   * Larghezza scelta per la scheda del task, di fianco all'elenco (sezione Task).
   *
   * In `localStorage` e non in memoria: è una preferenza di come si vuole vedere la pagina, e
   * ritrovarla cambiata a ogni ricaricamento sarebbe come ritrovare la finestra ridimensionata.
   *
   * Fra la **roba della persona** e non fra le preferenze del dispositivo, come `kanbanCollapsed`
   * e i limiti WIP: su un browser condiviso la misura che si è scelta non deve valere per chi
   * viene dopo. Che la misura dipenda anche dallo schermo non la rende del dispositivo — chi ha
   * quello schermo è una persona.
   */
  taskPanelWidth: 'maefwk10:task-panel-w',
  kanbanCollapsed: 'maefwk10:kanban-collapsed',
  /* Se l'albero della WBS si apre tutto o tutto chiuso. Chi progetta vuole vedere tutto, chi
     controlla vuole le sole radici: sono due modi di lavorare, non una preferenza di comodo. */
  wbsCollapsed: 'maefwk10:wbs-collapsed',
  /* In che unità si leggono i tempi della WBS: minuti, ore o giornate. Il dato resta ore. */
  wbsTimeUnit: 'maefwk10:wbs-time-unit',
  /* L'ultima scheda aperta in «Utenti e ruoli» (Persone o Ruoli): chi assegna ruoli torna su Persone,
     chi cura il catalogo su Ruoli (01-11-2026). */
  permissionsTab: 'maefwk10:permissions-tab',
  /* L'ultima scheda aperta in «Presenze del personale» (Oggi, Per sede, Atteso del team): la pagina
     si riapre lì (03-11-2026). */
  presenceTab: 'maefwk10:presence-tab',
  /* L'ultima vista aperta nella sezione Task (Agenda, Lista, Kanban…): rientrando su `/tasks` si
     riapre lì (05-11-2026). Vedi `features/tasks/useRememberedTaskView.ts`. */
  tasksView: 'maefwk10:tasks-view',
  kanbanWipLimits: 'maefwk10:kanban-wip-limits',
  warehouseSite: 'maefwk10:warehouse:site',
  warehouseSiteLocations: 'maefwk10:warehouse:site-locations',
  warehouseLotsItem: 'maefwk10:warehouse:lots-item',
  reportingPresets: 'reporting-presets',
  reportingHistory: 'reporting-history',
  generalCrudHistory: 'general-crud-history',
  spotlightRecent: 'spotlight-recent',
  articlesBookmarks: 'articles-bookmarks',
  /*
   * LA BARRA DI NAVIGAZIONE DEL TELEFONO — forma e comportamento, scelti dalla persona.
   *
   * ⚠️ **Sono chiavi condivise con Lares**, e non e' una simmetria estetica: `usePreferenzaUtente`
   * usa la stessa stringa come `prefKey` sul server, quindi e' la stessa riga di
   * `user-preferences` dello stesso utente. Con le stesse chiavi la scelta fatta in Lares vale
   * qui e viceversa; con chiavi diverse la stessa persona deve decidere due volte, e non c'e'
   * nessun errore che glielo dica.
   *
   * ⚠️ **Il 19-09-2026 sono passate all'inglese qui, e Lares non e' ancora allineato.** Erano
   * `ui.barraForma`, `ui.barraRitiro`, `ui.barraVoci` — i nomi che Lares usa tuttora. Finche'
   * non cambia anche la', i due prodotti scrivono righe diverse: chi sceglie la forma della
   * barra in Lares qui la ritrova al predefinito, e nessuno dei due lo segnala. La corrispondenza
   * da riportare in Lares — righe gia' scritte comprese — sta in
   * `docs/FE-A-LARES-CHIAVI-PREFERENZE-v1.md`.
   *
   * ⚠️ Rompono la convenzione del resto di questo elenco (`maefwk10:…`), e di proposito: qui la
   * convenzione la dettano i dati condivisi, non il repo.
   */
  barShape: 'ui.barShape',
  barAutoHide: 'ui.barAutoHide',
  /* Quali pagine ci stanno: percorsi separati da virgola. È la terza chiave condivisa, con lo
     stesso formato di Lares — vedi `shared/mobile/vociBarra.ts` per il perché sono tre chiavi e
     non un oggetto. */
  barItems: 'ui.barItems',
  /* Con quale superficie si apre il dettaglio: modale, drawer o colonna in pagina.
     Vedi `shared/detail/surfacePreference.ts`. */
  detailSurface: 'maefwk10:detail-surface',
  /* La testata delle pagine si apre aperta o chiusa. Nome dettato dal BE. */
  pageHeaderVisible: 'ui.pageHeaderVisible',
  /* «Turni di lavoro» in modalità semplificata o avanzata: la sceglie chi pianifica, di solito
     una persona o un ufficio per azienda, quindi basta la preferenza personale. */
  shiftsMode: 'maefwk10:shifts-mode',
  /* Il flusso le cui colonne mostra la bacheca; vuoto = quello del gruppo. */
  boardWorkflow: 'maefwk10:board-workflow',
  /* Cosa la persona nasconde della home e in che ordine. Vedi `features/home/homeLayout.ts`. */
  homeLayout: 'maefwk10:home-layout',
  /* «Personalizza la home»: gli interruttori nell'ordine dell'elenco o raggruppati per modulo. */
  homeCustomizerGrouping: 'maefwk10:home-customizer-grouping',
} as const;

/**
 * LE CHIAVI INTESTATE A UNA PERSONA — `shared/lib/storage.ts` ci mette l'id utente davanti.
 *
 * ## Perché l'elenco è qui e non accanto alle chiavi
 *
 * Perché la domanda «di chi è questa roba?» va risposta **in un posto solo**. Sparsa nelle feature
 * diventa una convenzione, e una convenzione sull'intestatario di un dato si perde alla terza
 * pagina nuova: si scrive una chiave, funziona, e la separazione fra due persone dipende da chi si
 * è ricordato di un prefisso.
 *
 * ## Cosa ci sta e cosa no
 *
 * Ci sta ciò che **una persona ha scelto o composto**: i preferiti, i preset di Reporting, i
 * modelli delle comunicazioni, i segnalibri, le ricerche recenti, le colonne chiuse del kanban, la
 * sede di magazzino su cui sta lavorando.
 *
 * Non ci sta ciò che è **del dispositivo**: l'impronta, il blocco biometrico, l'e-mail ricordata
 * al login, il tema, la cache del branding. Sono cose di quel telefono o di quel PC, e intestarle
 * le romperebbe: l'e-mail ricordata serve *prima* che si sappia chi entra, e il tema del tenant è
 * lo stesso per tutti.
 *
 * ⚠️ Non ci sta nemmeno la coda delle timbrature, ed è l'unica esclusione che vale una spiegazione:
 * vedi il commento su `attendanceQueue` qui sopra.
 */
/**
 * Le chiavi intestate a una persona: `storage.ts` ci mette davanti l'id utente, cosi' su un
 * dispositivo condiviso il secondo non eredita le cose del primo.
 *
 * ⚠️ **Restano qui anche quelle che dal 03-09-2026 vivono sul server** (`usePreferenzaUtente`), e
 * non e' una dimenticanza: quello che resta in `localStorage` e' lo **specchio** — serve al primo
 * render e alla rete assente — e uno specchio di roba della persona e' roba della persona. Se
 * uscissero da questo elenco, sul tablet di reparto il secondo utente vedrebbe le scelte del
 * primo per il tempo che la risposta del server impiega ad arrivare.
 */
export const PERSONAL_KEYS = [
  STORAGE_KEYS.favoriteProjects,
  STORAGE_KEYS.taskPanelWidth,
  STORAGE_KEYS.kanbanCollapsed,
  STORAGE_KEYS.wbsCollapsed,
  STORAGE_KEYS.wbsTimeUnit,
  STORAGE_KEYS.permissionsTab,
  STORAGE_KEYS.presenceTab,
  STORAGE_KEYS.tasksView,
  STORAGE_KEYS.kanbanWipLimits,
  STORAGE_KEYS.warehouseSite,
  STORAGE_KEYS.warehouseSiteLocations,
  STORAGE_KEYS.warehouseLotsItem,
  STORAGE_KEYS.reportingPresets,
  STORAGE_KEYS.reportingHistory,
  STORAGE_KEYS.generalCrudHistory,
  STORAGE_KEYS.spotlightRecent,
  STORAGE_KEYS.articlesBookmarks,
  /* La forma e il comportamento della barra del telefono: sono del **singolo utente**, quindi su
     un browser condiviso si separano da sole. Vedi `shared/mobile/aspettoBarra.ts`. */
  STORAGE_KEYS.barShape,
  STORAGE_KEYS.barAutoHide,
  STORAGE_KEYS.barItems,
  /* Come si apre il dettaglio: sceglie la persona, non il dispositivo. */
  STORAGE_KEYS.detailSurface,
  STORAGE_KEYS.pageHeaderVisible,
  STORAGE_KEYS.shiftsMode,
  STORAGE_KEYS.boardWorkflow,
  STORAGE_KEYS.homeLayout,
  STORAGE_KEYS.homeCustomizerGrouping,
] as const;

export const JSON_API_CONTENT_TYPE = 'application/vnd.api+json';

export const DEFAULT_PAGE_SIZE = 25;

export const PLATFORMS = ['web', 'ios', 'android'] as const;
export type Platform = (typeof PLATFORMS)[number];
